<template>
  <v-container fluid>
    <v-row align="center" justify="center" no-gutters>
      <v-col cols="12">
        <v-toolbar color="white" flat>
          <v-toolbar-title class="text-h5">角色管理</v-toolbar-title>
        </v-toolbar>
      </v-col>
      <v-col cols="12">
        <v-card outlined>
          <v-card-actions class="pb-0 px-4">
            <v-subheader class="px-0">角色列表</v-subheader>
            <v-spacer></v-spacer>
            <v-btn outlined small color="primary" @click.stop="newItem">新增</v-btn>
          </v-card-actions>

          <v-card-text>
            <v-data-table
              :loading="loading"
              :headers="headers"
              :items="roleList"
              :items-per-page="20"
              :footer-props="{itemsPerPageOptions: [10,20,50,100]}"
            >
              <template v-slot:item.code="{ item }">
                <v-edit-dialog
                  :return-value.sync="item.code"
                  cancel-text="取消"
                  save-text="保存"
                  large
                  persistent
                  @save="saveProps({id: item.id, code: item.code})"
                >
                  <div>{{item.code}}</div>
                  <template v-slot:input>
                    <v-text-field
                      class="pt-6"
                      v-model="item.code"
                      :rules="[max32chars, codeFormat]"
                      label="角色代码"
                      outlined
                      counter="32"
                      autofocus
                    ></v-text-field>
                  </template>
                </v-edit-dialog>
              </template>

              <template v-slot:item.name="{ item }">
                <v-edit-dialog
                  :return-value.sync="item.name"
                  cancel-text="取消"
                  save-text="保存"
                  large
                  persistent
                  @save="saveProps({id: item.id, name: item.name})"
                >
                  <div>{{item.name}}</div>
                  <template v-slot:input>
                    <v-text-field
                      class="pt-6"
                      v-model="item.name"
                      :rules="[max32chars]"
                      label="角色名称"
                      outlined
                      counter="32"
                      autofocus
                    ></v-text-field>
                  </template>
                </v-edit-dialog>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-icon small class="mr-2" @click.stop="editPerm(item)">mdi-pencil</v-icon>
                    </span>
                  </template>
                  <span>修改</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-icon small @click.stop="openDelete(item)">mdi-delete</v-icon>
                    </span>
                  </template>
                  <span>删除</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="editDialog.open" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">新增角色</span>
        </v-card-title>
        <v-card-text class="pb-0">
          <v-form v-model="editDialog.valid" ref="form">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    :rules="[notEmpty, max32chars, codeFormat]"
                    label="角色代码"
                    counter="32"
                    autofocus
                    dense
                    v-model="editDialog.code"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    :rules="[notEmpty, max32chars]"
                    label="角色名称"
                    counter="32"
                    dense
                    v-model="editDialog.name"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="editDialog.open = false">取消</v-btn>
          <v-btn color="blue darken-1" text :loading="editDialog.loading" :disabled="editDialog.loading"
                 @click="saveItem">保存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog.open" max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">删除角色</span>
        </v-card-title>
        <v-card-text class="pb-0">
          是否确认要删除：<br>
          {{ deleteDialog.item.name }} （{{ deleteDialog.item.code }}）
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="deleteDialog.open = false">取消</v-btn>
          <v-btn color="error" text :loading="deleteDialog.loading" :disabled="deleteDialog.loading"
                 @click="deleteItem">删除
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="permDialog.open" fullscreen hide-overlay transition="dialog-bottom-transition">
      <permission-dialog
        @alert="showAlert"
        @close="permDialog.open = false"
        @save="savePerm"
        :role-data="permDialog.item"
        :status="permDialog.open"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { buildAxios } from '@/plugins/axios'
import { handleResponse } from '@/plugins/errorHandler'
import PermissionDialog from '@/components/PermissionDialog'

export default {
  name: 'Role',
  components: { PermissionDialog },
  data () {
    return {
      loading: true,
      headers: [
        {
          text: 'ID',
          value: 'id'
        },
        {
          text: '代码',
          value: 'code'
        },
        {
          text: '名称',
          value: 'name'
        },
        {
          text: '操作',
          value: 'actions',
          sortable: false
        }
      ],
      notEmpty: v => !!v || '不能为空',
      max32chars: v => v.length <= 32 || '不能超过32个字符',
      codeFormat: v => /^[A-Za-z0-9_\\-]+$/.test(v) || '只能包含字母数字和 - _',
      roleList: [],
      editDialog: {
        open: false,
        valid: true,
        loading: false,
        code: '',
        name: ''
      },
      deleteDialog: {
        open: false,
        loading: false,
        item: {}
      },
      permDialog: {
        open: false,
        item: {}
      }
    }
  },
  created () {
    this.init()
  },
  methods: {
    showAlert (arg) {
      this.$emit('alert', arg)
    },
    init () {
      const request = buildAxios()
      request.get('role/getAll')
        .then(res => {
          this.roleList = res.data.data
          this.loading = false
        })
        .catch(reason => {
          this.loading = false
          this.$emit('alert', {
            msg: handleResponse(reason.response),
            type: 'error'
          })
        })
      this.$store.dispatch('getPermApi')
    },
    saveProps (arg) {
      const request = buildAxios()
      request.post('role/modify', arg)
        .then(() => {
          this.$emit('alert', {
            msg: '修改成功！',
            type: 'success'
          })
        })
        .catch(reason => {
          this.$emit('alert', {
            msg: handleResponse(reason.response),
            type: 'error'
          })
        })
    },
    newItem () {
      this.editDialog.open = true
      this.editDialog.code = ''
      this.editDialog.name = ''
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    saveItem () {
      this.$refs.form.validate()
      if (this.editDialog.valid) {
        this.editDialog.loading = true
        const request = buildAxios()
        request.post('role/add', this.editDialog)
          .then(res => {
            this.roleList.push(res.data.data)
            this.$emit('alert', {
              msg: '角色添加成功！',
              type: 'success'
            })
            this.editDialog.open = false
            this.editDialog.loading = false
          })
          .catch(reason => {
            this.editDialog.loading = false
            this.$emit('alert', {
              msg: handleResponse(reason.response),
              type: 'error'
            })
          })
      }
    },
    openDelete (item) {
      this.deleteDialog.item = item
      this.deleteDialog.open = true
    },
    deleteItem () {
      this.deleteDialog.loading = true
      const request = buildAxios()
      request.post('role/delete', this.deleteDialog.item)
        .then(res => {
          const dr = res.data.data
          this.roleList = this.roleList.filter(r => r.id !== dr.id)
          this.$emit('alert', {
            msg: '角色删除成功！',
            type: 'success'
          })
          this.deleteDialog.open = false
          this.deleteDialog.loading = false
        })
        .catch(reason => {
          this.deleteDialog.loading = false
          this.$emit('alert', {
            msg: handleResponse(reason.response),
            type: 'error'
          })
        })
    },
    editPerm (item) {
      this.permDialog.item = item
      this.permDialog.open = true
    },
    savePerm (arg) {
      const idx = this.roleList.findIndex(r => r.id === arg.id)
      this.$set(this.roleList, idx, arg)

      this.permDialog.open = false
      this.$emit('alert', {
        msg: '权限修改成功！',
        type: 'success'
      })
    }
  }
}
</script>
